import { useState, useEffect, useRef } from "react";
import {
  Database,
  Upload,
  RefreshCw,
  Trash2,
  Ship,
  Mail,
  Building2,
  Globe,
  AlertTriangle,
  Check,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPanel() {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeduplicating, setIsDeduplicating] = useState(false);
  const [uploadMode, setUploadMode] = useState("merge");
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("user");
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      toast.error("Failed to load database statistics");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [".csv", ".xlsx", ".xls"];
    const extension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    
    if (!validTypes.includes(extension)) {
      toast.error("Please upload a CSV or Excel file");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `${API_URL}/admin/upload-database?mode=${uploadMode}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success(
        `Database updated! ${response.data.records_inserted} records ${
          uploadMode === "merge" ? "merged" : "inserted"
        }, ${response.data.records_skipped} skipped.`
      );
      fetchStats();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload database");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDeduplicate = async () => {
    setIsDeduplicating(true);
    try {
      const response = await axios.post(`${API_URL}/admin/deduplicate`);
      toast.success(
        `Deduplication complete! ${response.data.duplicates_removed} duplicates removed.`
      );
      fetchStats();
    } catch (error) {
      console.error("Deduplicate error:", error);
      toast.error("Failed to deduplicate database");
    } finally {
      setIsDeduplicating(false);
    }
  };

  const handleClearDatabase = async () => {
    try {
      await axios.delete(`${API_URL}/admin/clear-database`);
      toast.success("Database cleared successfully");
      fetchStats();
    } catch (error) {
      console.error("Clear database error:", error);
      toast.error("Failed to clear database");
    }
  };

  const handleCreateUser = async () => {
    if (!newUsername || !newPassword) {
      toast.error("Username and password are required");
      return;
    }
    setIsCreatingUser(true);
    try {
      await axios.post(`${API_URL}/admin/users`, {
        username: newUsername,
        password: newPassword,
        role: newRole,
      });
      toast.success("User created successfully");
      setNewUsername("");
      setNewPassword("");
      setNewRole("user");
    } catch (error) {
      console.error("Create user error:", error);
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setIsCreatingUser(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-8">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="stats-card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-ocean-100 rounded-lg flex items-center justify-center">
              <Ship className="w-6 h-6 text-ocean-700" />
            </div>
            <div>
              <p className="text-2xl font-bold text-ocean-900 font-mono">
                {isLoading ? (
                  <span className="skeleton inline-block w-16 h-6 rounded" />
                ) : (
                  stats?.total_vessels?.toLocaleString() || 0
                )}
              </p>
              <p className="text-sm text-slate-500">Total Vessels</p>
            </div>
          </div>
        </div>

        <div className="stats-card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Mail className="w-6 h-6 text-green-700" />
            </div>
            <div>
              <p className="text-2xl font-bold text-ocean-900 font-mono">
                {isLoading ? (
                  <span className="skeleton inline-block w-16 h-6 rounded" />
                ) : (
                  stats?.unique_emails?.toLocaleString() || 0
                )}
              </p>
              <p className="text-sm text-slate-500">Unique Emails</p>
            </div>
          </div>
        </div>

        <div className="stats-card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-purple-700" />
            </div>
            <div>
              <p className="text-2xl font-bold text-ocean-900 font-mono">
                {isLoading ? (
                  <span className="skeleton inline-block w-16 h-6 rounded" />
                ) : (
                  stats?.unique_companies?.toLocaleString() || 0
                )}
              </p>
              <p className="text-sm text-slate-500">Companies</p>
            </div>
          </div>
        </div>

        <div className="stats-card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Globe className="w-6 h-6 text-blue-700" />
            </div>
            <div>
              <p className="text-2xl font-bold text-ocean-900 font-mono">
                {isLoading ? (
                  <span className="skeleton inline-block w-16 h-6 rounded" />
                ) : (
                  stats?.unique_countries?.toLocaleString() || 0
                )}
              </p>
              <p className="text-sm text-slate-500">Countries</p>
            </div>
          </div>
        </div>
      </div>

      {/* Database Management */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Upload Database */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-signal-orange/10 rounded-lg flex items-center justify-center">
              <Database className="w-5 h-5 text-signal-orange" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl text-ocean-900">
                Upload Master Database
              </h2>
              <p className="text-sm text-slate-500">
                Import vessel data from CSV or Excel
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Upload Mode
              </label>
              <Select value={uploadMode} onValueChange={setUploadMode}>
                <SelectTrigger
                  data-testid="upload-mode-select"
                  className="w-full"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="merge">
                    Merge - Add new records, update existing
                  </SelectItem>
                  <SelectItem value="replace">
                    Replace - Clear database and import fresh
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div
              className={`upload-zone cursor-pointer ${isUploading ? "active" : ""}`}
              onClick={() => !isUploading && fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="admin-file-input"
                disabled={isUploading}
              />

              {isUploading ? (
                <div className="flex flex-col items-center">
                  <Loader2 className="w-10 h-10 text-signal-orange animate-spin mb-3" />
                  <p className="text-ocean-900 font-medium">
                    Processing database...
                  </p>
                  <p className="text-sm text-slate-500">
                    This may take a few minutes for large files
                  </p>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                  <p className="text-ocean-900 font-medium mb-1">
                    Click to upload database file
                  </p>
                  <p className="text-sm text-slate-500">
                    CSV or Excel with vessel data
                  </p>
                </>
              )}
            </div>

            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                <strong>Expected columns:</strong> Vessel Name, Company Name, IMO #,
                Confirm Email, Country, Port
              </p>
            </div>
          </div>
        </div>

        {/* User Management */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl text-ocean-900">
                Create User
              </h2>
              <p className="text-sm text-slate-500">
                Add users with role-based access
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Username
              </label>
              <input
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ocean-900/20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ocean-900/20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Role
              </label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">
                    User - Single Search & Bulk Upload
                  </SelectItem>
                  <SelectItem value="admin">Admin - Full Access</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleCreateUser}
              disabled={isCreatingUser}
              className="w-full h-11 bg-ocean-900 hover:bg-ocean-900/90 text-white font-semibold"
            >
              {isCreatingUser ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create User"
              )}
            </Button>
          </div>
        </div>

        {/* Database Actions */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-ocean-100 rounded-lg flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-ocean-700" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl text-ocean-900">
                Database Actions
              </h2>
              <p className="text-sm text-slate-500">
                Manage and maintain your database
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Last Updated */}
            <div className="p-4 bg-slate-50 rounded-lg flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700">Last Updated</p>
                <p className="text-sm text-slate-500">
                  {formatDate(stats?.last_updated)}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchStats}
                disabled={isLoading}
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${isLoading ? "animate-spin" : ""}`}
                />
                Refresh
              </Button>
            </div>

            {/* Deduplicate */}
            <div className="p-4 border border-slate-200 rounded-lg">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-ocean-900">Remove Duplicates</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Find and remove duplicate vessel records based on IMO number
                  </p>
                </div>
                <Button
                  data-testid="deduplicate-button"
                  variant="outline"
                  onClick={handleDeduplicate}
                  disabled={isDeduplicating}
                  className="flex-shrink-0"
                >
                  {isDeduplicating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Deduplicate
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Clear Database */}
            <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-red-900 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Clear Database
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    Permanently delete all vessel records. This action cannot be
                    undone.
                  </p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      data-testid="clear-database-button"
                      variant="destructive"
                      size="sm"
                      className="flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clear All
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Clear Vessel Database?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete all{" "}
                        {stats?.total_vessels?.toLocaleString() || 0} vessel
                        records from the database. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        data-testid="confirm-clear-button"
                        onClick={handleClearDatabase}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Yes, Clear Database
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-ocean-900 text-white rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Database className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg mb-2">
              Database Best Practices
            </h3>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>
                • Use <strong>Merge</strong> mode to add new vessels without losing
                existing data
              </li>
              <li>
                • Run <strong>Deduplication</strong> after merging to remove
                duplicates
              </li>
              <li>
                • Ensure your upload file has proper column headers (Vessel Name,
                IMO #, Email, etc.)
              </li>
              <li>• Backup your data before using Replace mode</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
